import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  UserCircle, Search, RefreshCw, Phone, Calendar, Car, MapPin, Navigation, Map, List
} from "lucide-react";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

// Leaflet imports for cluster map
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export const AllCustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [rideFilter, setRideFilter] = useState("all");
  const [viewMode, setViewMode] = useState("list"); // "list" or "map"
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersLayerRef = useRef(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/superadmin/all-customers`);
      setCustomers(res.data.customers || res.data || []);
    } catch (err) {
      toast.error("Failed to fetch customers");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-IN', { 
      day: 'numeric', month: 'short', year: 'numeric' 
    });
  };

  // Filter customers
  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = 
      customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone?.includes(searchTerm) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRideFilter = rideFilter === "all" || 
      (rideFilter === "with_rides" ? (customer.total_rides || 0) > 0 : (customer.total_rides || 0) === 0);
    
    return matchesSearch && matchesRideFilter;
  });

  // Get customers with valid locations for map
  const customersWithLocation = filteredCustomers.filter(
    c => c.first_ride_pickup?.lat && c.first_ride_pickup?.lng
  );

  // Group customers by approximate location for clustering
  const getLocationKey = (lat, lng) => {
    // Round to ~1km precision for clustering
    return `${lat.toFixed(2)},${lng.toFixed(2)}`;
  };

  // Initialize/update map when view mode changes or data changes
  useEffect(() => {
    if (viewMode !== "map" || !mapRef.current || customersWithLocation.length === 0) {
      return;
    }

    // Initialize map if not already done
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current).setView([20.5937, 78.9629], 5); // Center on India
      
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(mapInstanceRef.current);
      
      markersLayerRef.current = L.layerGroup().addTo(mapInstanceRef.current);
    }

    // Clear existing markers
    if (markersLayerRef.current) {
      markersLayerRef.current.clearLayers();
    }

    // Group customers by location for clustering
    const locationGroups = {};
    customersWithLocation.forEach(c => {
      const key = getLocationKey(c.first_ride_pickup.lat, c.first_ride_pickup.lng);
      if (!locationGroups[key]) {
        locationGroups[key] = {
          lat: c.first_ride_pickup.lat,
          lng: c.first_ride_pickup.lng,
          customers: [],
          totalRides: 0
        };
      }
      locationGroups[key].customers.push(c);
      locationGroups[key].totalRides += (c.total_rides || 0);
    });

    // Add cluster markers
    const bounds = [];
    Object.values(locationGroups).forEach(group => {
      const count = group.customers.length;
      const size = Math.min(50, 24 + count * 3); // Size based on customer count
      
      // Create custom icon with count
      const icon = L.divIcon({
        className: 'custom-cluster-icon',
        html: `<div style="
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: white;
          border-radius: 50%;
          width: ${size}px;
          height: ${size}px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: ${Math.min(14, 10 + count)}px;
          border: 3px solid white;
          box-shadow: 0 2px 10px rgba(0,0,0,0.3);
        ">${count}</div>`,
        iconSize: [size, size],
        iconAnchor: [size/2, size/2]
      });

      const marker = L.marker([group.lat, group.lng], { icon })
        .bindPopup(`
          <div style="min-width: 180px;">
            <strong style="font-size: 14px;">${count} Customer${count > 1 ? 's' : ''}</strong><br/>
            <span style="font-size: 12px; color: #666;">
              Total Rides: ${group.totalRides}<br/>
              ${group.customers.slice(0, 3).map(c => c.name || 'Unknown').join(', ')}
              ${count > 3 ? `<br/>... and ${count - 3} more` : ''}
            </span>
          </div>
        `);
      
      markersLayerRef.current.addLayer(marker);
      bounds.push([group.lat, group.lng]);
    });

    // Fit bounds to show all markers
    if (bounds.length > 0) {
      mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
    }

    return () => {
      // Cleanup on unmount
      if (mapInstanceRef.current && viewMode !== "map") {
        // Don't destroy, just hide
      }
    };
  }, [viewMode, customersWithLocation.length]);

  // Cleanup map on unmount
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <div data-testid="all-customers-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">All Customers</h1>
          <p className="text-muted-foreground">View customers and their ride history</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex items-center border rounded-lg overflow-hidden">
            <Button 
              variant={viewMode === "list" ? "default" : "ghost"} 
              size="sm"
              onClick={() => setViewMode("list")}
              className="rounded-none"
              data-testid="view-list-btn"
            >
              <List className="w-4 h-4 mr-1" />
              List
            </Button>
            <Button 
              variant={viewMode === "map" ? "default" : "ghost"} 
              size="sm"
              onClick={() => setViewMode("map")}
              className="rounded-none"
              data-testid="view-map-btn"
            >
              <Map className="w-4 h-4 mr-1" />
              Map
            </Button>
          </div>
          <Button variant="outline" onClick={fetchCustomers} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search name, phone, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        
        <Select value={rideFilter} onValueChange={setRideFilter}>
          <SelectTrigger data-testid="ride-filter">
            <SelectValue placeholder="Filter by Rides" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Customers</SelectItem>
            <SelectItem value="with_rides">With Rides</SelectItem>
            <SelectItem value="no_rides">No Rides Yet</SelectItem>
          </SelectContent>
        </Select>
        
        <div className="flex items-center justify-end text-sm text-muted-foreground">
          Showing {filteredCustomers.length} of {customers.length} customers
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="text-sm text-blue-700">
          <strong>Note:</strong> Customers are not assigned to any specific admin. 
          They can book rides with any available driver based on their location.
          {viewMode === "map" && customersWithLocation.length > 0 && (
            <span className="ml-2">
              <strong>{customersWithLocation.length}</strong> customers with location data shown on map.
            </span>
          )}
        </p>
      </div>

      {/* Map View */}
      {viewMode === "map" && (
        <div className="mb-6">
          <div 
            ref={mapRef} 
            className="w-full h-[500px] rounded-xl border border-border overflow-hidden"
            style={{ zIndex: 1 }}
            data-testid="customer-map"
          />
          {customersWithLocation.length === 0 && !loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-card/80 rounded-xl">
              <div className="text-center">
                <MapPin className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No customers with location data</p>
              </div>
            </div>
          )}
          {/* Map Legend */}
          <div className="mt-4 flex items-center justify-center gap-4 text-sm">
            <span className="text-muted-foreground">Customer Clusters:</span>
            <div className="flex items-center gap-1">
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>1</div>
              <span>Single</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>5+</div>
              <span>Multiple</span>
            </div>
            <span className="text-muted-foreground ml-4">Click markers for details</span>
          </div>
        </div>
      )}

      {/* Customers Table */}
      {viewMode === "list" && (loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredCustomers.length === 0 ? (
        <div className="bg-card rounded-xl border border-border p-8 text-center">
          <UserCircle className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">No customers found</p>
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>First Pickup Location</TableHead>
                <TableHead>First Ride City</TableHead>
                <TableHead>Total Rides</TableHead>
                <TableHead>Total Spent</TableHead>
                <TableHead>Last Ride</TableHead>
                <TableHead>Joined</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCustomers.map((customer) => (
                <TableRow key={customer.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <UserCircle className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{customer.name || "Unknown"}</p>
                        <p className="text-xs text-muted-foreground">{customer.email || "No email"}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <p className="flex items-center gap-1 text-sm">
                      <Phone className="w-3 h-3" />
                      {customer.phone}
                    </p>
                  </TableCell>
                  <TableCell>
                    {customer.first_ride_pickup?.lat ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <a 
                              href={`https://www.google.com/maps?q=${customer.first_ride_pickup.lat},${customer.first_ride_pickup.lng}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                            >
                              <Navigation className="w-3 h-3" />
                              {customer.first_ride_pickup.lat.toFixed(4)}, {customer.first_ride_pickup.lng.toFixed(4)}
                            </a>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">{customer.first_ride_pickup.address || "Click to open in Maps"}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <span className="text-xs text-muted-foreground">No rides yet</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {customer.first_ride_city ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                        <MapPin className="w-3 h-3" />
                        {customer.first_ride_city}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Car className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{customer.total_rides || 0}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">₹{(customer.total_spent || 0).toLocaleString('en-IN')}</span>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {customer.last_ride_at ? formatDate(customer.last_ride_at) : "Never"}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDate(customer.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ))}
    </div>
  );
};

export default AllCustomersPage;
